/**
 * Worklist data hooks (Owner/Admin, worklist:read). The four lists come from ZPCD views so their
 * columns vary by kind and money columns are stripped server-side for a worklist-but-no-money role —
 * the UI tolerates absent columns and never assumes a money field is present.
 *
 * All four kinds are loaded together for the dashboard's health-band strip (a count per kind), but
 * each list also re-reads from the same cache so a per-kind view is free.
 */

import { useQueries, useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useRbac } from "@/context/RbacContext";
import { WORKLIST_KINDS } from "@/api/types";
import type { Worklist, WorklistKind, WorklistSummary } from "@/api/types";

// Re-exported for the screens that render one tile per kind. The list itself lives in api/types.ts,
// where WorklistKind is derived from it — see the note there.
export { WORKLIST_KINDS };

export const worklistKeys = {
  one: (kind: WorklistKind) => ["worklist", kind] as const,
};

export function useWorklist(kind: WorklistKind) {
  const { hasPermission } = useRbac();
  return useQuery({
    queryKey: worklistKeys.one(kind),
    queryFn: ({ signal }) => api.worklist(kind, signal),
    enabled: hasPermission("worklist:read"),
    staleTime: 60_000,
  });
}

/** The add-on seat-waste worklist (purpose-built typed rows; money columns stripped by role). Gated on
 *  worklist:read like the four generic lists; kept separate so it isn't forced through the generic table. */
export function useAddonWaste() {
  const { hasPermission } = useRbac();
  return useQuery({
    queryKey: ["worklist", "addon_waste"] as const,
    queryFn: ({ signal }) => api.addonWasteWorklist(signal),
    enabled: hasPermission("worklist:read"),
    staleTime: 60_000,
  });
}

/**
 * Lapsed trials — the PROSPECT list (v_lapsed_trial). Purpose-built typed rows, kept out of the generic
 * WORKLIST_KINDS. Carries no money at all, so there is nothing for strip_money to remove.
 */
export function useLapsedTrial() {
  const { hasPermission } = useRbac();
  return useQuery({
    queryKey: ["worklist", "lapsed_trial"] as const,
    queryFn: ({ signal }) => api.lapsedTrialWorklist(signal),
    enabled: hasPermission("worklist:read"),
    staleTime: 60_000,
  });
}

/** Load all four kinds for the dashboard strip + lists. Returns per-kind queries in a stable order.
 *  Still used by the Worklists page (DashboardPage) for its per-kind row tables — the Home stat tiles
 *  moved to `useWorklistSummary` below (one round trip instead of five). Do not delete this: the
 *  per-kind tables read live rows, not just a count, and the summary endpoint returns counts only. */
export function useAllWorklists() {
  const { hasPermission } = useRbac();
  const enabled = hasPermission("worklist:read");
  return useQueries({
    queries: WORKLIST_KINDS.map((kind) => ({
      queryKey: worklistKeys.one(kind),
      queryFn: ({ signal }: { signal: AbortSignal }) => api.worklist(kind, signal),
      enabled,
      staleTime: 60_000,
    })),
  });
}

export const worklistSummaryKey = ["worklist", "summary"] as const;

/**
 * One round trip for every worklist count + the mv_account_360 refresh freshness — replaces the five
 * parallel `useAllWorklists` calls on Home (2026-07-24 worklist matview refactor). `refreshed_at`/`stale`
 * come straight from the server (§3 of the refactor brief); render them via
 * `FreshnessBadge kind="worklist"`, never the `mirror`/`mirror-health` badge — a DIFFERENT freshness fact.
 */
export function useWorklistSummary() {
  const { hasPermission } = useRbac();
  return useQuery<WorklistSummary>({
    queryKey: worklistSummaryKey,
    queryFn: ({ signal }) => api.worklistSummary(signal),
    enabled: hasPermission("worklist:read"),
    staleTime: 60_000,
  });
}

/** Pick the first present string field from a row, tolerating the varying ZPCD column names. */
export function pickString(row: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
}

/** Pick the first present numeric field from a row. */
export function pickNumber(row: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "number" && !Number.isNaN(v)) return v;
  }
  return null;
}

/**
 * Pick a STRICT boolean field — used for the `is_active` retirement flag on the badge worklists.
 *
 * Returns undefined when the key is absent or not a boolean; it NEVER coerces. That matters: the whole
 * point of the flag is `false` = retired, and a truthiness test would read a missing key as "retired" (or,
 * worse, a `"false"` string as ACTIVE). RetiredBadge only fires on an exact `false`, so undefined = no
 * badge = the fail-safe direction.
 */
export function pickBoolean(row: Record<string, unknown>, key: string): boolean | undefined {
  const v = row[key];
  return typeof v === "boolean" ? v : undefined;
}

export type { Worklist };
